import React, { useState, useEffect } from 'react';
import { ucFirst } from '../../common/ambient';
import classNames from 'classnames';
import Avatar from '../user/Avatar';
import { alertSetOk } from '.'
import bindAllActions from '../../common/bindAllActions'
import { path } from 'ramda'

const AlertSetMemberCard = ({ member, editing, alertSets, onChange, onCancel, socialActions, user, ...props }) => {
  alertSets = alertSets || []
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    alertPrefs: [],
    alertSets: [],
    admin: false
  });
  const [alertSetState, setAlertSetState] = useState({
    _id: '',
    alertPrefs: []
  })

  const [validationError, setValidationError] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [sendingEmailInvite, setSendingEmailInvite] = useState(false);

  // Helper function to send email verification
  const sendEmailVerification = (memberData, onSuccess, onError) => {
    setSendingEmailInvite(true);

    socialActions.addUserAction({
      type: 'alert-set-group-member-email',
      data: {
        memberId: member._id,
        email: memberData.email || formState.email,
        name: memberData.name || formState.name || 'Group Member'
      }
    })
    .then(res => {
      setSendingEmailInvite(false);
      if (res && res._id && onSuccess) {
        onSuccess(res);
      }
    })
    .catch(err => {
      setSendingEmailInvite(false);
      console.error('Failed to create user action:', err);
      if (onError) {
        onError(err);
      }
    });
  };

  // Check if current form state is different from original member data
  const hasChanges = () => {
    return formState.name !== (member.name || '') ||
           formState.email !== (member.email || '') ||
           formState.phone !== (member.phone || '') ||
           JSON.stringify(formState.alertSets) !== JSON.stringify(member.alertSets) ||
           JSON.stringify(formState.alertPrefs.sort()) !== JSON.stringify((member.alertPrefs || []).sort());
  };
  
  // Validate phone number has 10 digits
  const isValidPhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };
  
  // Helper to validate if contact method is valid based on invite method
  const hasValidContact = () => {
    // For email invites, require email 
    if (member.inviteMethod === 'email') {
      return formState.email && formState.email.trim() !== '';
    } 
    // For SMS invites, require valid phone
    else if (member.inviteMethod === 'sms') {
      return formState.phone && formState.phone.trim() !== '' && isValidPhone(formState.phone);
    }
    // For existing or non-specified invites, check if either is valid
    else {
      return (formState.email && formState.email.trim() !== '') || 
             (formState.phone && formState.phone.trim() !== '' && isValidPhone(formState.phone));
    }
  };

  useEffect(() => {
    setFormState({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      alertPrefs: member.alertPrefs || [],
      alertSets: member.alertSets || [],
      admin: member.admin || false,
      inviteMethod: member.inviteMethod // Include the invite method in the form state
    });
    setValidationError('');
  }, [member]);

  const handleCheckboxChange = (pref, prop = 'alertPrefs') => {
    setFormState(prev => {
      const newAlertPrefs = prev[prop].includes(pref)
        ? prev[prop].filter(p => p !== pref)
        : [...prev[prop], pref];
      
      return {
        ...prev,
        [prop]: newAlertPrefs
      };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate form after each input change
    if (name === 'email' || name === 'phone') {
      validateForm({ ...formState, [name]: value });
    }
  };
  
  // Centralized validation function
  const validateForm = (form) => {
    // For email invites, require email
    if (member.inviteMethod === 'email') {
      if (!form.email) {
        setValidationError('Email address is required');
      } else {
        setValidationError('');
      }
    } 
    // For SMS invites, require valid phone
    else if (member.inviteMethod === 'sms') {
      if (!form.phone) {
        setValidationError('Phone number is required');
      } else if (!isValidPhone(form.phone)) {
        setValidationError('Phone number must have 10 digits');
      } else {
        setValidationError('');
      }
    }
    // For existing or non-specified invites, check if either is valid
    else {
      if (!form.email && (!form.phone || !isValidPhone(form.phone))) {
        setValidationError('Either email or valid 10-digit phone number is required');
      } else if (form.phone && form.phone.trim() !== '' && !isValidPhone(form.phone)) {
        setValidationError('Phone number must have 10 digits');
      } else {
        setValidationError('');
      }
    }
  };

  const handleSave = () => {
    // Validate before saving
    validateForm(formState);
    
    if (!hasValidContact()) {
      return; // Don't proceed if validation fails
    }
    
    // Create the updated member object
    const updatedMember = {
      ...member,
      ...formState
    };
    
    // Clear uaId if the phone was changed
    if (member.clearUaId) {
      updatedMember.uaId = null;
      delete updatedMember.clearUaId;
    }
    
    // For SMS invites, automatically send the SMS when saving for the first time
    if (member.inviteMethod === 'sms' && formState.phone && isValidPhone(formState.phone) && !member.uaId) {
      setSendingInvite(true);
      
      // Create a user action of type alert-set-group-member-sms
      socialActions.addUserAction({
        type: 'alert-set-group-member-sms',
        data: {
          memberId: member._id,
          phone: formState.phone,
          name: formState.name || 'Group Member'
        }
      })
      .then(res => {
        setSendingInvite(false);
        // Update member with the uaId from the created userAction and save
        if (res && res._id) {
          const finalMember = {
            ...updatedMember,
            uaId: res._id,
            phoneInvitePending: true
          };
          onChange(finalMember);
        } else {
          onChange(updatedMember);
        }
      })
      .catch(err => {
        setSendingInvite(false);
        console.error('Failed to create user action:', err);
        alert('Failed to send SMS invite. The member will be saved but you may need to send the invite later.');
        onChange(updatedMember);
      });
    }
    // For Email invites, automatically send the email when saving for the first time
    else if (member.inviteMethod === 'email' && formState.email && !member.uaId) {
      sendEmailVerification(
        formState,
        (res) => {
          // Update member with the uaId from the created userAction and save
          const finalMember = {
            ...updatedMember,
            uaId: res._id,
            emailVerified: false,
            emailInvitePending: true
          };
          onChange(finalMember);
        },
        () => {
          alert('Failed to send email invite. The member will be saved but you may need to send the invite later.');
          onChange(updatedMember);
        }
      );
    }
    // When an existing member changes their email, require re-verification
    else if (member.email && formState.email !== member.email && member.emailVerified) {
      sendEmailVerification(
        formState,
        (res) => {
          // Reset email verification status and save with new uaId
          const finalMember = {
            ...updatedMember,
            uaId: res._id,
            emailVerified: false, // Reset to unverified since email changed
            emailInvitePending: true
          };
          onChange(finalMember);
        },
        () => {
          alert('Failed to send email verification. The member will be saved but you may need to send the invite later.');
          // Still save with reset verification status
          const finalMember = {
            ...updatedMember,
            emailVerified: false,
            emailInvitePending: false
          };
          onChange(finalMember);
        }
      );
    } else {
      // For all other cases, just save normally
      onChange(updatedMember);
    }
  };

  const handleCancel = () => {
    setFormState({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      alertPrefs: member.alertPrefs || [],
      alertSets: member.alertSets || [],
      admin: member.admin || false,
      inviteMethod: member.inviteMethod // Preserve the invite method
    });
    setValidationError('');
    onCancel();
  };

  const renderField = (label, value, fieldName) => {
    // If value is not set, don't render the field at all in non-editing mode
    if (!value) {
      return null;
    }
    
    let extraContent = null;
    
    // For email fields with a value
    if (fieldName === 'email' && value) {
      if (member.admin) {
        // For admin users, check if their email is verified in user object
        if (path(['info', 'isVerified'], user)) {
          // Email is verified
          extraContent = <span className={`invited-status accepted`}></span>;
        }
      }
      // For non-admin members
      else if (member.emailVerified) {
        // Email is already verified - show accepted status
        extraContent = <span className={`invited-status accepted`}></span>;
      } 
      else if (member.emailInvitePending || (member.uaId && member.inviteMethod === 'email')) {
        // Email invite is pending verification (either original or added later)
        extraContent = <span className={`invited-status`}>pending</span>;
      }
      // For SMS users with unverified email who don't have a pending email verification
      else if (member.inviteMethod === 'sms' && member.phoneVerified && !member.emailVerified && !member.emailInvitePending) {
        extraContent = sendingEmailInvite ? (
          <span className="sending-invite">sending...</span>
        ) : (
          <a
            className="send-invite-link"
            onClick={() => {
              sendEmailVerification(
                member,
                (res) => {
                  if (onChange) {
                    onChange({
                      ...member,
                      uaId: res._id,
                      emailVerified: false,
                      emailInvitePending: true
                    });
                  }
                },
                () => {
                  alert('Failed to send email invite. Please try again.');
                }
              );
            }}
          >
            send invite
          </a>
        );
      }
    }
    
    // For phone fields with a value
    if (fieldName === 'phone' && value) {
      if (member.admin) {
        // For admin users, check if their phone is verified in user object
        if (!path(['info', 'phoneConfirmKey'], user)) {
          // Phone is verified
          extraContent = <span className={`invited-status accepted`}></span>;
        }
      }
      // For non-admin members
      else if (member.phoneVerified) {
        // Phone is already verified - show accepted status
        extraContent = <span className={`invited-status accepted`}></span>;
      }
      else if (member.phoneInvitePending || (member.uaId && member.inviteMethod === 'sms')) {
        // SMS invite is pending verification (either original or added later)
        extraContent = <span className={`invited-status`}>pending</span>;
      } 
      // Only show send SMS invite link for email invites with verified email who don't have a pending SMS verification
      // as SMS invites will automatically send the invite when saved
      else if (member.inviteMethod === 'email' && member.emailVerified && !member.phoneVerified && !member.phoneInvitePending) {
        extraContent = sendingInvite ? (
          <span className="sending-invite">sending...</span>
        ) : (
          <a 
            className="send-invite-link"
            onClick={() => {
              setSendingInvite(true);
              
              // Create a user action of type alert-set-group-member-sms
              socialActions.addUserAction({
                type: 'alert-set-group-member-sms',
                data: {
                  memberId: member._id,
                  phone: member.phone,
                  name: member.name || 'Group Member'
                }
              })
              .then(res => {
                setSendingInvite(false);
                // Update member with the uaId from the created userAction
                if (onChange && res && res._id) {
                  onChange({
                    ...member,
                    uaId: res._id,
                    phoneInvitePending: true
                  });
                }
              })
              .catch(err => {
                setSendingInvite(false);
                console.error('Failed to create user action:', err);
                alert('Failed to send invite. Please try again.');
              });
            }}
          >
            send invite
          </a>
        );
      }
    }
    
    return (
      <div className="form-line">
        <label>{label}:</label>
        <span className="value">{value}</span>
        {extraContent && <small className="action-links">&nbsp;{extraContent}</small>}
      </div>
    );
  };

  const editingAndNotAdmin = editing && !formState.admin;

  const renderAlertPrefs = () => {
    if (editing) {
      return (
        <div className="form-line alert-prefs">
          <label>Alert Preferences</label>
          <div className="checkbox-group">
            {['email', 'sms', 'app'].map(pref => (
              <label key={pref} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formState.alertPrefs.includes(pref)}
                  onChange={() => handleCheckboxChange(pref)}
                />
                {ucFirst(pref)}
              </label>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="form-line">
        <label>Alert Preferences:</label>
        <span className="value">
          {formState.alertPrefs.length > 0 
            ? formState.alertPrefs.join(', ') 
            : 'None set'}
        </span>
      </div>
    );
  };
  const renderAlertSets = () => {
    const alertOptions = ['email']
    if (member.phone) {
      alertOptions.push('sms')
    }
    const remainingAlertSets = alertSets
      .filter(_as => !formState.alertSets.find(mas => mas._id === _as._id))
      .filter(_as => alertSetOk(_as))
    const prefClasses = {
      email: 'glyphicon glyphicon-envelope',
      sms: 'glyphicon glyphicon-comment',
    }
    
    // Check if the member has either verified email or verified phone
    const hasEmailOrVerifiedPhone = member.admin ? 
      // For admin users, check user object for verification status
      (formState.email && path(['info', 'isVerified'], user)) || (formState.phone && !path(['info', 'phoneConfirmKey'], user)) :
      // For regular members
      (formState.email && member.emailVerified) || (formState.phone && member.phoneVerified)

    // If in editing mode and no email/verified phone, don't render alert sets section at all
    if (editing && !hasEmailOrVerifiedPhone && !member.admin) {
      return null
    }
    
    // If not editing and no alert sets, don't show this section at all (we'll show a button in AlertSets instead)
    if (!editing && (!formState.alertSets || formState.alertSets.length === 0)) {
      return null
    }
    
    return (
      <div className="form-line render-alert-sets">
        <label>Alert Sets</label>
        <div>
          {/* Only show alert set selector in edit mode if they have email or verified phone */}
          {editing && remainingAlertSets.length > 0 && (
            <>
              <select className='form-control' onChange={e => {
                setAlertSetState({
                  ...alertSetState,
                  _id: e.target.value
                })
              }}>
                <option value="">Select Alert Set</option>
                {remainingAlertSets.map((set, i) => (
                    <option key={i} value={set._id}>{set.title}</option>
                ))}
              </select>
              <div className="form-line alert-prefs">
                <div className="checkbox-group">
                  {alertOptions.map(pref => (
                    <label key={pref} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={alertSetState.alertPrefs && alertSetState.alertPrefs.includes(pref)}
                        onChange={() => {
                          setAlertSetState({
                            ...alertSetState,
                            alertPrefs: alertSetState.alertPrefs.includes(pref)
                              ? alertSetState.alertPrefs.filter(p => p !== pref)
                              : [...alertSetState.alertPrefs, pref]
                          })
                        }}
                      />
                      <i className={prefClasses[pref]} />
                      &nbsp;
                      {ucFirst(pref)}
                    </label>
                  ))}
                </div>
              </div>
              {alertSetState._id && alertSetState.alertPrefs.length > 0 && (
                <a
                  className="add-alert-set"
                  onClick={() => {
                    setFormState({
                      ...formState,
                      alertSets: [...formState.alertSets, alertSetState]
                    })
                    setAlertSetState({
                      _id: '',
                      alertPrefs: []
                    })
                  }}
                >Add Alert Set</a>
              )}
            </>
          )}
          <div className="member-alert-sets">
            {formState.alertSets.map((as, i) => {
              const alertSet = alertSets.find(_as => _as._id === as._id)
              const title = alertSet ? alertSet.title : 'alert set removed'
              return (
                <div className={classNames('member-alert-set alert-description', { inactive: !alertSet })} key={i}>
                  <span>{title}</span>
                  <div className='tools'>
                    {alertOptions.map(pref => {
                      const isActive = as.alertPrefs.includes(pref);
                      if (editing && hasEmailOrVerifiedPhone) {
                        // In edit mode - clickable icons
                        return (
                        <a 
                            key={pref}
                            onClick={() => {
                              const newAlertSets = formState.alertSets.map(alertSet => {
                                if (alertSet._id === as._id) {
                                  return {
                                    ...alertSet,
                                    alertPrefs: isActive
                                      ? alertSet.alertPrefs.filter(p => p !== pref)
                                      : [...alertSet.alertPrefs, pref]
                                  };
                                }
                                return alertSet;
                              });
                              setFormState({
                                ...formState,
                                alertSets: newAlertSets
                              });
                            }}
                          >
                            <i className={classNames(prefClasses[pref], { active: isActive })} />
                          </a>
                        );
                      }
                      // In view mode - just display icons
                      return (
                        <i
                          key={pref}
                          className={classNames(prefClasses[pref], { active: isActive })}
                        />
                      );
                    })}
                    {editing && hasEmailOrVerifiedPhone && (
                      <a className="remove-alert-set" onClick={() => {
                        setFormState({
                          ...formState,
                          alertSets: formState.alertSets.filter(mas => mas._id !== as._id)
                        })
                      }} >
                        <i className="active glyphicon glyphicon-trash trash" />
                      </a>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classNames("group-member-card", { editing, 'not-editing': !editing })}>
      <header>
        <Avatar name={!formState.admin && (formState.name || '?')} />
        {formState.admin ? (
          <span className="member-name">Group Admin (You)</span>
        ) : (
          editingAndNotAdmin ? (
            <div className="form-line">
              <input
                type="text"
                name="name"
                className="form-control"
                value={formState.name}
                onChange={handleInputChange}
                placeholder="Enter member name"
              />
            </div>
          ) : (
            <span className="member-name">{formState.name || 'Unnamed Member'}</span>
          )
        )}
      </header>
      
      {editingAndNotAdmin ? (
        <>
          {/* Show email field if inviteMethod is 'email' or if this is an SMS invite with verified phone */}
          {(member.inviteMethod === 'email' || formState.email || (member.inviteMethod === 'sms' && member.phoneVerified)) && (
            <div className={classNames("form-line", { error: validationError && validationError.includes('email') })}>
              <label>Email</label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={formState.email}
                onChange={handleInputChange}
                placeholder="Enter email address"
              />
            </div>
          )}

          {/* Show phone field if:
              - inviteMethod is 'sms' (SMS invitation flow)
              - phone is already present in the member data
              - inviteMethod is 'email' AND email is verified */}
          {(member.inviteMethod === 'sms' ||
            formState.phone ||
            (member.inviteMethod === 'email' && member.emailVerified)) && (
            <div className={classNames("form-line", { error: validationError && validationError.includes('phone') })}>
              <label>Phone</label>
              {(member.phoneInvitePending || (member.uaId && member.inviteMethod === 'sms')) && formState.phone ? (
                <div className="phone-invited">
                  <span className="value">{formState.phone}</span>
                  {member.phoneVerified 
                    ? <span className="invited-badge accepted"></span>
                    : <span className="invited-badge">pending</span>
                  }
                  <a 
                    className="change-phone-link"
                    onClick={() => {
                      if (window.confirm('This will remove the SMS invite. Continue?')) {
                        setFormState({
                          ...formState,
                          phone: ''
                        });
                        // When we save, we'll need to clear the uaId
                        // Create a modified member object to use when saving
                        member.clearUaId = true;
                      }
                    }}
                  >
                    change
                  </a>
                </div>
              ) : (
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  value={formState.phone}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit phone number"
                />
              )}
            </div>
          )}
          
          {validationError && (
            <div className="form-line error">
              <span className="error-message">{validationError}</span>
            </div>
          )}
        </>
      ) : (
        <>
          {renderField('Email', formState.email, 'email')}
          {renderField('Phone', formState.phone, 'phone')}
        </>
      )}

      {renderAlertSets()}

      {editing && (
        <div className="btns">
          {hasChanges() && (
            <>
              {/* Only show save button for email invite when both name and email are valid */}
              {member.inviteMethod === 'email' && formState.name && formState.email && !validationError && (
                <a 
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Save
                </a>
              )}
              
              {/* Only show save button for SMS invite when both name and phone are valid */}
              {member.inviteMethod === 'sms' && formState.name && formState.phone && isValidPhone(formState.phone) && !validationError && (
                <a 
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Save
                </a>
              )}
              
              {/* For other cases (regular members or admin), use existing logic */}
              {!member.inviteMethod && (
                <a 
                  className={classNames("btn btn-primary", { 'disabled': !hasValidContact() || validationError })}
                  onClick={hasValidContact() && !validationError ? handleSave : null}
                >
                  Save
                </a>
              )}
            </>
          )}
          <a 
            className="" 
            onClick={handleCancel}
          >
            Cancel
          </a>
        </div>
      )}
    </div>
  );
};

export default bindAllActions(AlertSetMemberCard);

AlertSetMemberCard.propTypes = {};
AlertSetMemberCard.defaultProps = {};
AlertSetMemberCard.displayName = 'AlertSetMemberCard'
